import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AIBot({ crop }) {
  const [prompt, setPrompt] = useState('');
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [showTables, setShowTables] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    loadTables();
    loadTemplates();
  }, [crop]);

  const loadTables = async () => {
    try {
      let url = `${API_BASE_URL}/ai/tables`;
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await axios.get(url);
      setAvailableTables(response.data.tables);
    } catch (err) {
      setError('Failed to load tables: ' + err.message);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ai/query-templates`);
      setTemplates(response.data.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const toggleTableSelection = (tableName) => {
    if (selectedTables.includes(tableName)) {
      setSelectedTables(selectedTables.filter(t => t !== tableName));
    } else {
      setSelectedTables([...selectedTables, tableName]);
    }
  };

  const insertTableIntoPrompt = (tableName) => {
    setPrompt(prompt + (prompt && !prompt.endsWith(' ') ? ' ' : '') + tableName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError(null);
    setQueryResult(null);
    setCurrentPage(1);

    try {
      const response = await axios.post(`${API_BASE_URL}/ai/query`, {
        prompt: prompt,
        selected_tables: selectedTables.length > 0 ? selectedTables : null
      });
      
      setQueryResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error processing query: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newPage) => {
    if (!queryResult || newPage < 1 || newPage > queryResult.total_pages) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/ai/query/paginated`, {
        sql_query: queryResult.sql_query,
        page: newPage,
        page_size: pageSize
      });
      
      setQueryResult(response.data);
      setCurrentPage(newPage);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading page: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (newPageSize) => {
    if (!queryResult) return;

    setPageSize(newPageSize);
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/ai/query/paginated`, {
        sql_query: queryResult.sql_query,
        page: 1,
        page_size: newPageSize
      });
      
      setQueryResult(response.data);
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error changing page size: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadResultsAsCSV = async () => {
    if (!queryResult || !queryResult.results) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch all results without pagination
      const response = await axios.post(`${API_BASE_URL}/ai/query/paginated`, {
        sql_query: queryResult.sql_query,
        page: 1,
        page_size: queryResult.total_count // Get all rows
      });

      const allResults = response.data.results;
      const columnNames = response.data.column_names;

      let csvContent = columnNames.join(',') + '\n';
      
      allResults.forEach(row => {
        const rowData = columnNames.map(col => {
          const value = row[col];
          if (value === null || value === undefined) return '';
          // Escape values that contain commas
          const strValue = String(value);
          return strValue.includes(',') ? `"${strValue}"` : strValue;
        });
        csvContent += rowData.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `ai_query_results_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Error downloading CSV: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <h2>🤖 AI Query Assistant</h2>
        <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
          Ask questions in natural language and get SQL-powered insights from your genomic data
        </p>

        {error && (
          <div className="error" style={{ marginBottom: '15px' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setShowTables(!showTables)}
            className="btn"
            style={{ marginBottom: '10px', width: '100%' }}
          >
            {showTables ? '▼' : '▶'} Browse Tables ({availableTables.length} available)
          </button>

          {showTables && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '10px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '4px',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              {availableTables.map(table => (
                <div
                  key={table}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px'
                  }}
                >
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px',
                    background: selectedTables.includes(table) ? '#dbf8f4' : 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedTables.includes(table)}
                      onChange={() => toggleTableSelection(table)}
                      style={{ marginRight: '6px' }}
                    />
                    {table}
                  </label>
                  <button
                    onClick={() => insertTableIntoPrompt(table)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: '#025c4f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    Insert
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedTables.length > 0 && (
          <div style={{
            padding: '10px',
            background: '#dbf8f4',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <strong>Selected tables for context:</strong> {selectedTables.join(', ')}
          </div>
        )}

        {/* Query Templates */}
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="btn"
            style={{ marginBottom: '10px', width: '100%', background: '#34495e' }}
          >
            {showTemplates ? '▼' : '▶'} 💡 Query Templates & Examples
          </button>

          {showTemplates && templates.length > 0 && (
            <div style={{
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '4px',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              {templates.map((category, idx) => (
                <div key={idx} style={{ marginBottom: '15px' }}>
                  <h4 style={{ color: '#025c4f', marginBottom: '10px', fontSize: '14px' }}>
                    {category.category}
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '8px'
                  }}>
                    {category.templates.map((template, tidx) => (
                      <button
                        key={tidx}
                        onClick={() => setPrompt(template.prompt)}
                        style={{
                          padding: '8px 12px',
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '13px',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#dbf8f4';
                          e.target.style.borderColor = '#025c4f';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'white';
                          e.target.style.borderColor = '#ddd';
                        }}
                      >
                        <div style={{ fontWeight: 'bold', color: '#025c4f', marginBottom: '4px' }}>
                          {template.label}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          "{template.prompt}"
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Your Question:
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Example: Show me all rows from phenotype_data where avling is greater than 5000"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                fontSize: '14px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <button
            type="submit"
            className="btn"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {loading ? '⏳ Processing...' : '🚀 Generate & Execute Query'}
          </button>
        </form>
      </div>

      {queryResult && (
        <div className="card">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>Query Results</h3>
            {queryResult.results.length > 0 && (
              <button
                onClick={downloadResultsAsCSV}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#025c4f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#01443a'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#025c4f'}
              >
                📥 Download CSV
              </button>
            )}
          </div>

          {/* Warnings Section */}
          {queryResult.warnings && queryResult.warnings.length > 0 && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <strong style={{ color: '#856404' }}>⚠️ Performance Warnings:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#856404' }}>
                {queryResult.warnings.map((warning, idx) => (
                  <li key={idx} style={{ marginBottom: '4px' }}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{
            background: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <h4 style={{ marginTop: 0, color: '#34495e' }}>Generated SQL:</h4>
            <pre style={{
              background: '#2c3e50',
              color: '#ecf0f1',
              padding: '12px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '13px'
            }}>
              {queryResult.sql_query}
            </pre>
            <p style={{ marginBottom: 0, color: '#7f8c8d', fontSize: '14px' }}>
              <strong>Explanation:</strong> {queryResult.explanation}
              {queryResult.query_cost && (
                <span style={{ marginLeft: '15px', color: '#025c4f' }}>
                  | Dataset size: <strong>{queryResult.query_cost}</strong>
                </span>
              )}
            </p>
          </div>

          {/* Statistics Section */}
          {queryResult.statistics && Object.keys(queryResult.statistics).length > 0 && (
            <div style={{
              background: '#dbf8f4',
              border: '1px solid #025c4f',
              padding: '15px',
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <h4 style={{ marginTop: 0, color: '#025c4f' }}>📊 Numeric Column Statistics:</h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px'
              }}>
                {Object.entries(queryResult.statistics).map(([colName, stats]) => (
                  <div key={colName} style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #8bcec0'
                  }}>
                    <div style={{ fontWeight: 'bold', color: '#025c4f', marginBottom: '8px' }}>
                      {colName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#555' }}>
                      <div>Min: <strong>{stats.min}</strong></div>
                      <div>Max: <strong>{stats.max}</strong></div>
                      <div>Avg: <strong>{stats.avg}</strong></div>
                      <div>Count: <strong>{stats.count}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '15px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div>
              <strong>Showing:</strong> {((queryResult.page - 1) * queryResult.page_size) + 1} - {Math.min(queryResult.page * queryResult.page_size, queryResult.total_count)} of {queryResult.total_count} rows
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '14px' }}>
                Rows per page:
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    marginLeft: '8px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                  disabled={loading}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </label>
            </div>
          </div>

          {queryResult.total_pages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
              padding: '10px',
              background: '#f8f9fa',
              borderRadius: '4px'
            }}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={queryResult.page === 1 || loading}
                style={{
                  padding: '6px 12px',
                  background: queryResult.page === 1 ? '#e0e0e0' : '#3498db',
                  color: queryResult.page === 1 ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: queryResult.page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                ⏮ First
              </button>
              <button
                onClick={() => handlePageChange(queryResult.page - 1)}
                disabled={queryResult.page === 1 || loading}
                style={{
                  padding: '6px 12px',
                  background: queryResult.page === 1 ? '#e0e0e0' : '#025c4f',
                  color: queryResult.page === 1 ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: queryResult.page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                ◀ Previous
              </button>
              <span style={{ padding: '0 15px', fontWeight: 'bold' }}>
                Page {queryResult.page} of {queryResult.total_pages}
              </span>
              <button
                onClick={() => handlePageChange(queryResult.page + 1)}
                disabled={queryResult.page === queryResult.total_pages || loading}
                style={{
                  padding: '6px 12px',
                  background: queryResult.page === queryResult.total_pages ? '#e0e0e0' : '#025c4f',
                  color: queryResult.page === queryResult.total_pages ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: queryResult.page === queryResult.total_pages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next ▶
              </button>
              <button
                onClick={() => handlePageChange(queryResult.total_pages)}
                disabled={queryResult.page === queryResult.total_pages || loading}
                style={{
                  padding: '6px 12px',
                  background: queryResult.page === queryResult.total_pages ? '#e0e0e0' : '#3498db',
                  color: queryResult.page === queryResult.total_pages ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: queryResult.page === queryResult.total_pages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Last ⏭
              </button>
            </div>
          )}

          {queryResult.results.length > 0 ? (
            <div style={{ overflow: 'auto', maxHeight: '500px' }}>
              <table>
                <thead>
                  <tr>
                    {queryResult.column_names.map(col => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.results.map((row, idx) => (
                    <tr key={idx}>
                      {queryResult.column_names.map(col => (
                        <td key={col}>
                          {row[col] !== null && row[col] !== undefined
                            ? typeof row[col] === 'number'
                              ? row[col].toFixed(3)
                              : String(row[col])
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#7f8c8d' }}>No rows returned by the query.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AIBot;
